import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import Case from 'case';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo, AppButton, UnderConstruction, SubmissionLoader,
} from '@Components/Control';
import { AuthNavWrapper } from '@Components/Navigation';
import { WidgetRenderer, SearchDetails } from '@Components/Partial/Product/Dashboard';
import { withProductAuth } from '@Components/Layout';
import {
  handleQuery,
  handleSort,
  handleFilter,
  initializeReviewedRows,
  updateRowReviewStatus,
  updateMultipleRowsReviewStatus,
} from '@Helpers/controlFunctions';
import {
  initiateProductDashboardSearchResultsRequest,
  clearProductDashboardSearchResultsRequest,
  updateProductReviewSelectionsRequest,
  initiateProductDashboardSearchResultsMetaRequest,
  clearProductDashboardSearchResultsMetaRequest,
} from '@Actions';


const DashboardResultsPage = ({
  small,
  location,
  size,
  loading,
  submitLoading,
  fetchSearchResultsData,
  clearSearchResultsData,
  searchResultsData = null,
  updateSearchResultsReviewSelections,
  fetchSearchResultsMeta,
  clearSearchResultsMeta,
  searchResultsMeta = null,
  searchDisabled = false,
  paymentPending = false,
}) => {
  const { id: searchId } = useParams();
  const [reviewedData, setReviewedData] = React.useState([]);
  const [initialLaterCount, setInitialLaterCount] = React.useState(null);
  const [query, setQuery] = React.useState({
    id: searchId,
    paging: {
      to: 1,
      size: 10,
    },
  });
  const {
    theme: { page, button },
    isDarkMode,
    constructionImage,
    statusColorMap,
    disableSearchFeatures,
  } = useAppTheme();

  React.useEffect(() => {
    fetchSearchResultsData(searchId, { ...query });
    fetchSearchResultsMeta(searchId);

    return () => {
      clearSearchResultsData();
      clearSearchResultsMeta();
    };
  }, [query]);

  /* eslint-disable camelcase */
  const sendReviewedResults = () => {
    const filteredSelections = _.filter(
      reviewedData,
      ({ review_status, prev_status }) => review_status !== 'pending' && review_status !== prev_status,
    ).map(({ id, review_status, prev_status }) => ({
      id,
      review_status,
      prev_status,
    }));

    updateSearchResultsReviewSelections({
      data: filteredSelections,
      search_result_can_be_updated: true,
    }, () => {
      fetchSearchResultsData(searchId, { ...query });
    });
    clearSearchResultsData();
    setReviewedData([]);
    window.scrollTo(0, 0);
  };

  const laterCount = reviewedData && _.filter(reviewedData, (({ review_status }) => review_status === 'later')).length;
  let submitEnabled = reviewedData.some(({ review_status }) => review_status !== 'pending' && review_status !== 'later');

  if (laterCount !== initialLaterCount) {
    submitEnabled = reviewedData.some(({ review_status }) => review_status !== 'pending');
  }
  /* eslint-enable camelcase */

  const handleExportCSV = () => {
    fetchSearchResultsData(searchId, { ...query }, true);
  };

  const padding = '1.5rem';
  const pageTitle = searchResultsData?.search?.title || 'Search results';
  const searchCategory = searchResultsData?.search?.category;
  const subTitle = searchCategory ? `Results - ${Case.capital(searchCategory)}` : 'Results';

  const withBannerDetails = searchResultsData?.search?.details
    && searchResultsData.search.details.length >= 1;
  const withAttributes = searchResultsData?.search?.attributes
    && searchResultsData.search.attributes.length >= 1;
  const reviewable = searchResultsData?.data && (searchResultsData.data.find((dt) => dt.feature === 'review/update') !== undefined);
  const totalResults = searchResultsData?.data && searchResultsData.data.find((dt) => dt.label === 'Total infringements');

  const resultsPerPageOptions = [10, 15, 20, 25, 50];
  const underMaintenance = disableSearchFeatures || searchDisabled;
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const renderPageContent = () => {
    if (underMaintenance) {
      return (
        <Box flex align="center" justify="center" background={page.background}>
          <UnderConstruction
            imageUrl={constructionImage}
            buttonHighlight={page.highlight}
            header="Under Maintenance"
            message="System currently undergoing maintenance. Please check back later."
          />
        </Box>
      );
    }

    return (
      <Box flex background={usePageBg} pad={{ vertical: padding }}>
        <WidgetRenderer
          small={small}
          size={size}
          loading={loading}
          paymentPending={paymentPending}
          handleQuery={(parentKey, updateKey, value) => (
            handleQuery(query, setQuery, parentKey, updateKey, value, {
              id: searchId,
              paging: {
                to: 1,
                size: 10,
              },
            })
          )}
          handleSort={(sortValue, sortDirection) => (
            handleSort(query, setQuery, sortValue, sortDirection)
          )}
          handleFilter={(key, value, predicate, seperateValues, reset) => (
            handleFilter(query, setQuery, key, value, predicate, seperateValues, reset)
          )}
          filterSortMeta={searchResultsMeta}
          searchQuery={query}
          widgetData={searchResultsData?.data}
          padding={padding}
          selectedStatuses={reviewedData}
          initializeReviewedRows={(data) => (
            initializeReviewedRows(setReviewedData, data, setInitialLaterCount)
          )}
          updateRowReviewStatus={(editableId, selectedOption) => (
            updateRowReviewStatus(reviewedData, setReviewedData, editableId, selectedOption)
          )}
          updateMultipleRowsReviewStatus={(updateableRows, selectedOption) => (
            updateMultipleRowsReviewStatus(
              reviewedData, setReviewedData, updateableRows, selectedOption,
            )
          )}
          resultsPerPageOptions={resultsPerPageOptions}
          resultsPerPage={query.paging.size}
          currentPage={query.paging.to}
          handleExportCSV={handleExportCSV}
          totalResults={totalResults?.value}
          searchDetails={withAttributes && (
            <SearchDetails
              attributes
              small={small}
              details={searchResultsData?.search?.attributes}
              statusColorMap={statusColorMap}
              textColor={page.primaryText}
              highlightText={page.altHighlight}
              weight={600}
            />
          )}
        />
        {searchResultsData?.data && reviewable && (
          <Box
            width="100%"
            direction={small ? 'column' : 'row'}
            gap={small ? '1rem' : '2rem'}
            justify="between"
            align="center"
            pad={padding}
          >
            <Box width={small ? '100%' : '65%'}>
              <Text size="1rem" weight={500} color={page.primaryText}>
                We will automatically send a DMCA notice for results marked as
                infringements after submission.
              </Text>
            </Box>
            <Box pad={{ right: small ? '0rem' : '4rem' }}>
              <AppButton
                overrideHover
                width="10rem"
                disabled={!submitEnabled || (reviewedData.length === 0)}
                onClick={() => sendReviewedResults(reviewedData)}
                level="dynamicLarge"
                color={button.primaryText}
                bgColor={button.primaryBg}
                fontWeight={600}
                label="Submit Review"
                title={submitEnabled ? null : 'Please select a status option'}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle,
        bannerChildren: withBannerDetails ? (
          <SearchDetails
            small={small}
            details={searchResultsData.search.details}
            textColor={page.primaryText}
            statusColorMap={statusColorMap}
          />
        ) : null,
        bannerChildrenPosition: 'bottom',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Fetching CSV export..."
          contentWidth="20rem"
        />
      )}
      {renderPageContent()}
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    searchResultsData: state.productDashboard.productSearchResultsData,
    searchResultsMeta: state.productDashboard.productSearchResultsMeta,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchSearchResultsData: initiateProductDashboardSearchResultsRequest,
    clearSearchResultsData: clearProductDashboardSearchResultsRequest,
    updateSearchResultsReviewSelections: updateProductReviewSelectionsRequest,
    fetchSearchResultsMeta: initiateProductDashboardSearchResultsMetaRequest,
    clearSearchResultsMeta: clearProductDashboardSearchResultsMetaRequest,
  }, dispatch);
}

DashboardResultsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchSearchResultsData: PropTypes.func.isRequired,
  clearSearchResultsData: PropTypes.func.isRequired,
  updateSearchResultsReviewSelections: PropTypes.func.isRequired,
  fetchSearchResultsMeta: PropTypes.func.isRequired,
  clearSearchResultsMeta: PropTypes.func.isRequired,
  searchResultsData: PropTypes.shape({
    query: PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      paging: PropTypes.shape({
        to: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
        size: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired,
      }),
    }).isRequired,
    search: PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]).isRequired,
      title: PropTypes.string.isRequired,
      category: PropTypes.string,
      details: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.arrayOf(PropTypes.string.isRequired),
        ]).isRequired,
        formatValue: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.instanceOf(Date),
        ]),
        type: PropTypes.string.isRequired,
        unit: PropTypes.string,
      })),
      attributes: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.arrayOf(PropTypes.string.isRequired),
        ]).isRequired,
        type: PropTypes.string.isRequired,
        unit: PropTypes.string,
      })),
    }).isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.any.isRequired,
      type: PropTypes.string.isRequired,
      unit: PropTypes.string,
      feature: PropTypes.string,
    }).isRequired).isRequired,
  }),
  searchResultsMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })).isRequired,
    }).isRequired,
  }),
  searchDisabled: PropTypes.bool,
  paymentPending: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(DashboardResultsPage));
